import { getAssignmentTiming } from "./assignmentsHelpers";

const isMongoId = (value) =>
  typeof value === "string" && /^[a-f0-9]{24}$/i.test(value);

const resolveId = (value) => {
  if (!value) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();

    return isMongoId(trimmed) ? trimmed : null;
  }

  if (typeof value !== "object") return null;

  const candidate =
    value._id ??
    value.id ??
    value.$oid ??
    (typeof value.toHexString === "function" ? value.toHexString() : null);

  if (candidate && candidate !== value) return resolveId(candidate);

  return null;
};

const isSurveyPayload = (value) => {
  if (!value) return false;

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return false;

    try {
      const parsed = JSON.parse(trimmed);

      return Boolean(parsed?.responses);
    } catch {
      return false;
    }
  }

  return Boolean(value.responses) && !resolveId(value);
};

export const getApiErrorMessage = (error, fallback = "Request failed") => {
  const payload = error?.data ?? error?.error?.data;
  const message = payload?.message ?? payload?.error ?? error?.message;

  if (Array.isArray(message)) {
    return message.filter(Boolean).join(", ") || fallback;
  }

  if (typeof message === "string" && message.trim()) {
    return message;
  }

  return fallback;
};

export const resolveInterpreterId = (interpreterRecord) => {
  if (interpreterRecord == null || interpreterRecord === "") return null;

  if (typeof interpreterRecord === "string") {
    return isMongoId(interpreterRecord) ? interpreterRecord : null;
  }

  return interpreterRecord._id || interpreterRecord.interpreter?._id || null;
};

export const getInterpreterRecordId = (user) =>
  resolveId(user?.interpreter) ||
  resolveId(user?.data?.interpreter) ||
  resolveInterpreterId(user?.interpreter);

export const normalizeAssignmentStatus = (status) =>
  String(status || "")
    .toLowerCase()
    .replace(/\s+/g, "_");

export const isCompletedAssignmentStatus = (status) => {
  const normalized = normalizeAssignmentStatus(status);

  return (
    normalized === "complete" ||
    normalized === "completed" ||
    normalized === "finished" ||
    normalized === "submitted"
  );
};

const looksLikeAssignmentRecord = (item) => {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false;
  if (!item._id && !item.id) return false;

  return (
    item.status != null ||
    item.response != null ||
    item.responses != null ||
    item.question != null ||
    item.form != null ||
    item.translation != null ||
    item.assignment != null ||
    item.video != null ||
    item.deadline != null
  );
};

export const extractAssignmentsFromPayload = (payload, seen = new WeakSet()) => {
  if (payload == null) return [];

  if (typeof payload === "object") {
    if (seen.has(payload)) return [];
    seen.add(payload);
  }

  if (Array.isArray(payload)) {
    return payload.flatMap((item) => {
      if (looksLikeAssignmentRecord(item)) return [item];
      return extractAssignmentsFromPayload(item, seen);
    });
  }

  if (typeof payload !== "object") return [];

  const nestedKeys = [
    "data",
    "assignments",
    "videos",
    "items",
    "results",
    "records",
    "complete",
    "completed",
    "completed_assignments",
    "assigned",
    "incomplete",
    "not_assigned",
    "list",
  ];

  const collected = nestedKeys.flatMap((key) =>
    payload[key] == null
      ? []
      : extractAssignmentsFromPayload(payload[key], seen),
  );

  if (!collected.length && looksLikeAssignmentRecord(payload)) {
    return [payload];
  }

  const byId = new Map();
  collected.forEach((item) => {
    const key = String(item._id || item.id);
    if (!byId.has(key)) byId.set(key, item);
  });

  return Array.from(byId.values());
};

const SUBMITTED_STORAGE_PREFIX = "unzippd.interpreter.submittedAssignments.";
const SUBMITTED_MEMORY_KEY = "__unzippdSubmittedAssignmentsMemory";

const getSubmittedMemoryStore = () => {
  if (typeof window === "undefined") return new Map();
  if (!window[SUBMITTED_MEMORY_KEY]) {
    window[SUBMITTED_MEMORY_KEY] = new Map();
  }
  return window[SUBMITTED_MEMORY_KEY];
};

const submittedAssignmentsStorageKey = (id) =>
  `${SUBMITTED_STORAGE_PREFIX}${id}`;

const mergeAssignmentRecords = (...lists) => {
  const byId = new Map();

  lists.flat().forEach((item) => {
    if (!item?._id && !item?.id) return;
    const key = String(item._id || item.id);
    const existing = byId.get(key);
    if (!existing) {
      byId.set(key, item);
      return;
    }
    byId.set(key, {
      ...existing,
      ...item,
      status: isCompletedAssignmentStatus(item.status)
        ? item.status
        : existing.status,
    });
  });

  return Array.from(byId.values());
};

const readStoredAssignmentList = (id) => {
  if (!id || typeof window === "undefined") return [];

  const key = submittedAssignmentsStorageKey(id);

  try {
    const raw =
      window.localStorage?.getItem(key) || window.sessionStorage?.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const toSerializableAssignment = (record) => {
  const completedRecord = { ...record, status: "complete" };
  const seen = new WeakSet();

  try {
    return JSON.parse(
      JSON.stringify(completedRecord, (_key, value) => {
        if (typeof value === "object" && value !== null) {
          if (seen.has(value)) return undefined;
          seen.add(value);
        }
        return value;
      }),
    );
  } catch {
    return {
      _id: record._id,
      status: "complete",
      assignment: Array.isArray(record.assignment) ? record.assignment : [],
      organization: record.organization,
      question: record.question,
      form: record.form,
      response: record.response,
      responses: record.responses,
      translation: record.translation,
      date_created: record.date_created,
      completed_date: record.completed_date,
      deadline: record.deadline,
      video: record.video,
      videoUrl: record.videoUrl,
    };
  }
};

export const readRememberedSubmittedAssignments = (...ids) => {
  const keys = [...new Set(ids.filter(Boolean).map(String))];
  if (!keys.length) return [];

  const memory = getSubmittedMemoryStore();

  return mergeAssignmentRecords(
    ...keys.map((key) => memory.get(key) || []),
    ...keys.map((key) => readStoredAssignmentList(key)),
  );
};

export const rememberSubmittedAssignment = (
  interpreterId,
  record,
  extraIds = [],
) => {
  if (!record?._id) return [];

  const completedRecord = { ...record, status: "complete" };
  const serializable = toSerializableAssignment(completedRecord);
  const keys = [
    ...new Set([interpreterId, ...extraIds].filter(Boolean).map(String)),
  ];

  if (!keys.length) return [completedRecord];

  const memory = getSubmittedMemoryStore();
  let next = [completedRecord];

  keys.forEach((key) => {
    next = mergeAssignmentRecords(
      memory.get(key) || [],
      readStoredAssignmentList(key),
      [completedRecord],
    );
    memory.set(key, next);

    try {
      const stored = mergeAssignmentRecords(
        readStoredAssignmentList(key),
        [serializable],
      );
      window.localStorage?.setItem(
        submittedAssignmentsStorageKey(key),
        JSON.stringify(stored),
      );
    } catch {
      try {
        window.sessionStorage?.setItem(
          submittedAssignmentsStorageKey(key),
          JSON.stringify(stored),
        );
      } catch {
        // Keep the in-memory copy even if browser storage is unavailable.
      }
    }
  });

  return next;
};

export const getAssignedInterpreters = (assignment) =>
  Array.isArray(assignment?.assignment) ? assignment.assignment : [];

export const getAssignedInterpreterIds = (assignment) =>
  getAssignedInterpreters(assignment)
    .map((item) => resolveId(item) || item?._id)
    .filter(Boolean);

export const getAssignmentQuestionId = (assignment) => {
  const responseRecord = getAssignmentResponseRecord(assignment);

  return (
    resolveId(assignment?.question) ||
    resolveId(assignment?.extractedQuestion) ||
    resolveId(responseRecord?.question) ||
    resolveId(assignment?.question_id) ||
    null
  );
};

export const getAssignmentOrganizationId = (assignment) => {
  const responseRecord = getAssignmentResponseRecord(assignment);

  return (
    resolveId(assignment?.organization) ||
    resolveId(assignment?.extractedOrganization) ||
    resolveId(assignment?.question?.form?.organization) ||
    resolveId(assignment?.form?.organization) ||
    resolveId(responseRecord?.form?.organization) ||
    resolveId(responseRecord?.organization) ||
    resolveId(assignment?.organization_id) ||
    null
  );
};

export const getAssignmentFormId = (assignment) => {
  const responseRecord = getAssignmentResponseRecord(assignment);

  return (
    resolveId(assignment?.form) ||
    resolveId(assignment?.question?.form) ||
    resolveId(assignment?.extractedForm) ||
    resolveId(responseRecord?.form) ||
    resolveId(assignment?.form_id) ||
    null
  );
};

export const getAssignmentResponseRecord = (assignment) => {
  if (assignment?.responses?.[0] && resolveId(assignment.responses[0])) {
    return assignment.responses[0];
  }

  if (
    assignment?.response &&
    typeof assignment.response === "object" &&
    !isSurveyPayload(assignment.response) &&
    resolveId(assignment.response)
  ) {
    return assignment.response;
  }

  if (typeof assignment?.response === "string" && isMongoId(assignment.response)) {
    return {
      _id: assignment.response,
      question: assignment.question,
      form: assignment.form,
      organization: assignment.organization,
    };
  }

  if (assignment?.response_id) {
    return {
      _id: assignment.response_id,
      question: assignment.question,
      form: assignment.form,
    };
  }

  return null;
};

export const getAssignmentResponseId = (assignment) => {
  const responseRecord = getAssignmentResponseRecord(assignment);

  return (
    resolveId(responseRecord) ||
    (typeof assignment?.response === "string" && isMongoId(assignment.response)
      ? assignment.response
      : null) ||
    resolveId(assignment?.response_id) ||
    null
  );
};

export const getMissingVideoAssignmentFields = (assignment, interpreterId) =>
  [
    !interpreterId && "interpreter",
    !getAssignmentFormId(assignment) && "form",
    !getAssignmentQuestionId(assignment) && "question",
    !getAssignmentOrganizationId(assignment) && "organisation",
    !getAssignmentResponseId(assignment) && "response",
  ].filter(Boolean);

const getVideoList = (payload) => {
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload?.data)) return payload.data;

  return [];
};

export const findMatchingVideoResponse = (videos, assignment) => {
  const questionId = getAssignmentQuestionId(assignment);
  const formId = getAssignmentFormId(assignment);
  const organizationId = getAssignmentOrganizationId(assignment);
  const existingResponseId = getAssignmentResponseId(assignment);
  const list = getVideoList(videos);

  if (existingResponseId) {
    const exact = list.find(
      (video) => resolveId(video) === existingResponseId,
    );

    if (exact) return exact;
  }

  const scored = list
    .map((video) => {
      const videoQuestionId = resolveId(video?.question);
      const videoFormId = resolveId(video?.form);
      const videoOrgId =
        resolveId(video?.form?.organization) || resolveId(video?.organization);

      if (questionId && videoQuestionId && videoQuestionId !== questionId) {
        return null;
      }

      let score = 0;

      if (questionId && videoQuestionId === questionId) score += 4;

      if (formId && videoFormId === formId) score += 2;

      if (organizationId && videoOrgId === organizationId) score += 2;
      
      if (!video?.assigned_videos) score += 1;

      return score > 0 ? { video, score } : null;
    })
    .filter(Boolean)
    .sort((left, right) => right.score - left.score);

  return scored[0]?.video || null;
};

export const mergeAssignmentWithVideoResponse = (assignment, video) => {
  if (!assignment || !video) return assignment;

  return {
    ...assignment,
    response: video,
    form: assignment.form || video.form,
    question: assignment.question || video.question,
    organization:
      assignment.organization ||
      video.form?.organization ||
      video.organization,
  };
};

export const isInterpreterAlreadyAssigned = (assignment, interpreterId) => {
  if (!interpreterId) return false;

  const targetIds = new Set(
    [interpreterId].flat().filter(Boolean).map((value) => String(value)),
  );

  return getAssignedInterpreters(assignment).some((item) => {
    const itemIds = [
      resolveId(item),
      item?._id,
      resolveId(item?.user),
      item?.user?._id,
    ]
      .filter(Boolean)
      .map((value) => String(value));

    return itemIds.some((itemId) => targetIds.has(itemId));
  });
};

export const assignmentMatchesListFilters = (
  assignment,
  filters = {},
  extras = {},
) => {
  const interpreterId = filters.interpreter;
  const interpreterUserId = extras.interpreterUserId;
  const status = filters.status;
  const assignedCount = getAssignedInterpreters(assignment).filter((item) =>
    resolveId(item),
  ).length;
  const normalized = normalizeAssignmentStatus(assignment?.status);
  const isComplete = isCompletedAssignmentStatus(normalized);

  if (interpreterId || interpreterUserId) {
    const matchesInterpreter =
      isInterpreterAlreadyAssigned(assignment, interpreterId) ||
      isInterpreterAlreadyAssigned(assignment, interpreterUserId);

    if (!matchesInterpreter) return false;
  }

  if (!status || status === "all") return true;

  if (status === "assigned") {
    return assignedCount > 0 && !isComplete;
  }

  if (status === "not_assigned") {
    return assignedCount === 0 && !isComplete;
  }

  if (status === "complete") {
    return isComplete;
  }

  return normalized === status;
};

export const getInterpreterAssignmentPlan = ({
  assignment,
  interpreterId,
  isSecondCheck = false,
}) => {
  const assignedInterpreters = getAssignedInterpreters(assignment).filter(
    (item) => resolveId(item),
  );
  const questionId = getAssignmentQuestionId(assignment);
  const organizationId = getAssignmentOrganizationId(assignment);
  const formId = getAssignmentFormId(assignment);
  const responseId = getAssignmentResponseId(assignment);
  const responseRecord = getAssignmentResponseRecord(assignment);
  const assignmentId = assignment?._id;
  const hasExistingInterpreters = assignedInterpreters.length >= 1;
  const canCreateVideoAssignment =
    Boolean(formId && questionId && organizationId && responseId);

  if (assignmentId && (isSecondCheck || hasExistingInterpreters)) {
    const interpreterIds = [
      ...assignedInterpreters.map((item) => resolveId(item) || item._id),
      ...(isInterpreterAlreadyAssigned(assignment, interpreterId)
        ? []
        : [interpreterId]),
    ].filter(Boolean);

    return {
      type: "update",
      payload: {
        assignmentId,
        status: "assigned",
        assignment: interpreterIds,
        ...(assignment?.translation?.length
          ? { translation: assignment.translation }
          : {}),
        isSecondChecked: isSecondCheck || hasExistingInterpreters,
        ...(questionId ? { question: questionId } : {}),
        ...(organizationId ? { organization: organizationId } : {}),
        ...(responseId ? { response: responseId } : {}),
        ...(formId ? { form: formId } : {}),
      },
    };
  }

  if (canCreateVideoAssignment) {
    return {
      type: "create",
      responseRecord,
      questionId,
      organizationId,
      interpreterId,
    };
  }

  if (questionId && organizationId && !assignmentId) {
    return {
      type: "assignToQuestion",
      questionId,
      organizationId,
      interpreterId,
      assignmentId,
      responseId,
      formId,
    };
  }

  return {
    type: "invalid",
    message: `Cannot assign this video. Missing: ${
      getMissingVideoAssignmentFields(assignment, interpreterId).join(", ") ||
      "form, question, organisation, or response"
    }.`,
  };
};

export const buildVideoAssignmentPayload = ({ response, interpreterId }) =>
  buildVideoAssignmentPayloadFromAssignment({
    assignment: {
      form: response?.form,
      question: response?.question,
      organization: response?.form?.organization || response?.organization,
      response,
    },
    interpreterId,
  });

export const buildVideoAssignmentPayloadFromAssignment = ({
  assignment,
  interpreterId,
}) => {
  const form = getAssignmentFormId(assignment);
  const question = getAssignmentQuestionId(assignment);
  const organization = getAssignmentOrganizationId(assignment);
  const videoResponseId = getAssignmentResponseId(assignment);

  const missing = [
    !interpreterId && "interpreter",
    !form && "form",
    !question && "question",
    !organization && "organization",
    !videoResponseId && "response",
  ].filter(Boolean);

  if (missing.length) {
    throw new Error(
      `Cannot assign this video. Missing: ${missing.join(", ")}.`,
    );
  }

  return {
    form,
    question,
    organization,
    response: videoResponseId,
    assignment: [interpreterId],
  };
};

export const buildBulkAssignmentEntry = async ({
  response,
  interpreterId,
  videoRefs,
}) => {
  const timing = await getAssignmentTiming(response, videoRefs);

  return {
    ...buildVideoAssignmentPayload({ response, interpreterId }),
    ...timing,
  };
};

export const buildBulkAssignmentEntryFromAssignment = async ({
  assignment,
  interpreterId,
  videoRefs,
}) => {
  const responseRecord = getAssignmentResponseRecord(assignment);
  const timingFromVideo = responseRecord
    ? await getAssignmentTiming(responseRecord, videoRefs)
    : null;
  const measuredTotal = timingFromVideo?.total_time;
  const measuredAvg = timingFromVideo?.avg_time;

  return {
    ...buildVideoAssignmentPayloadFromAssignment({
      assignment,
      interpreterId,
    }),
    total_time:
      (measuredTotal && measuredTotal !== "00:00:00" ? measuredTotal : null) ||
      assignment?.total_time ||
      "00:00:01",
    avg_time:
      (measuredAvg && measuredAvg !== "00:00" ? measuredAvg : null) ||
      assignment?.avg_time ||
      "00:01",
  };
};
